import { Conversation, Message } from '../types/messages';

// Demo users for conversations
const demoUsers = [
  {
    user_id: 'user-1',
    username: 'sarah_johnson',
    full_name: 'Sarah Johnson',
    avatar_url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330',
    online: true,
    last_seen: new Date().toISOString()
  },
  {
    user_id: 'user-2',
    username: 'michael_chen',
    full_name: 'Michael Chen',
    avatar_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d',
    online: false,
    last_seen: new Date(Date.now() - 3600000).toISOString() // 1 hour ago
  },
  {
    user_id: 'user-3',
    username: 'alex_rodriguez',
    full_name: 'Alex Rodriguez',
    avatar_url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e',
    online: true,
    last_seen: new Date().toISOString()
  },
  {
    user_id: 'user-4',
    username: 'emily_patel',
    full_name: 'Emily Patel',
    avatar_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80',
    online: false,
    last_seen: new Date(Date.now() - 86400000).toISOString() // 1 day ago
  },
  {
    user_id: 'user-5',
    username: 'james_wilson',
    full_name: 'James Wilson',
    avatar_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e',
    online: false,
    last_seen: new Date(Date.now() - 1800000).toISOString() // 30 minutes ago
  }
];

// Current user
export const currentUser = {
  user_id: 'current-user',
  username: 'current_user',
  full_name: 'Current User',
  avatar_url: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde',
  online: true,
  last_seen: new Date().toISOString()
};

// Demo messages for each conversation
const generateMessages = (conversationId: string, otherUserId: string, count: number = 10): Message[] => {
  const messages: Message[] = [];
  const now = new Date();
  
  for (let i = 0; i < count; i++) {
    const isFromCurrentUser = i % 2 === 0;
    const timestamp = new Date(now.getTime() - (count - i) * 600000); // 10 minutes apart
    
    messages.push({
      id: `msg-${conversationId}-${i}`,
      content: isFromCurrentUser 
        ? `Hey, this is a message from you (message ${i + 1})` 
        : `This is a reply from the other person (message ${i + 1})`,
      sender_id: isFromCurrentUser ? currentUser.user_id : otherUserId,
      receiver_id: isFromCurrentUser ? otherUserId : currentUser.user_id,
      timestamp: timestamp.toISOString(),
      read: true,
    });
  }
  
  // Add one unread message for some conversations
  if (conversationId === 'conv-1' || conversationId === 'conv-3') {
    messages.push({
      id: `msg-${conversationId}-unread`,
      content: 'This is an unread message!',
      sender_id: otherUserId,
      receiver_id: currentUser.user_id,
      timestamp: new Date().toISOString(),
      read: false,
    });
  }
  
  return messages;
};

// Create demo conversations
export const demoConversations: Conversation[] = demoUsers.map((user, index) => {
  const conversationId = `conv-${index + 1}`;
  const messages = generateMessages(conversationId, user.user_id);
  
  return {
    id: conversationId,
    participants: [
      { ...currentUser },
      { ...user }
    ],
    last_message: messages[messages.length - 1],
    created_at: new Date(Date.now() - 7 * 86400000).toISOString(), // 7 days ago
    updated_at: messages[messages.length - 1].timestamp,
    is_group: false
  };
});

// Add one group conversation
const groupConversation: Conversation = {
  id: 'group-1',
  participants: [
    { ...currentUser },
    { ...demoUsers[0] },
    { ...demoUsers[1] },
    { ...demoUsers[2] }
  ],
  last_message: {
    id: 'msg-group-1-last',
    content: 'This is the latest group message!',
    sender_id: demoUsers[0].user_id,
    receiver_id: 'group-1',
    timestamp: new Date(Date.now() - 1200000).toISOString(), // 20 minutes ago
    read: true
  },
  created_at: new Date(Date.now() - 14 * 86400000).toISOString(), // 14 days ago
  updated_at: new Date(Date.now() - 1200000).toISOString(), // 20 minutes ago
  is_group: true,
  group_name: 'Project Team',
  group_avatar: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c'
};

// Add the group conversation to the demo conversations
export const allDemoConversations = [...demoConversations, groupConversation];

// Function to get all messages for a specific conversation
export const getMessagesForConversation = (conversationId: string): Message[] => {
  if (conversationId === 'group-1') {
    // Generate group messages
    return [
      {
        id: 'msg-group-1-1',
        content: 'Hey team, how is everyone doing?',
        sender_id: currentUser.user_id,
        receiver_id: 'group-1',
        timestamp: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
        read: true
      },
      {
        id: 'msg-group-1-2',
        content: 'Doing great! Working on the new features.',
        sender_id: demoUsers[0].user_id,
        receiver_id: 'group-1',
        timestamp: new Date(Date.now() - 3500000).toISOString(), // 58 minutes ago
        read: true
      },
      {
        id: 'msg-group-1-3',
        content: 'I need some help with the database schema.',
        sender_id: demoUsers[1].user_id,
        receiver_id: 'group-1',
        timestamp: new Date(Date.now() - 3400000).toISOString(), // 56 minutes ago
        read: true
      },
      {
        id: 'msg-group-1-4',
        content: 'I can help with that! Let me know when you\'re free.',
        sender_id: demoUsers[2].user_id,
        receiver_id: 'group-1',
        timestamp: new Date(Date.now() - 3300000).toISOString(), // 55 minutes ago
        read: true
      },
      {
        id: 'msg-group-1-5',
        content: 'Thanks! How about we meet at 3 PM?',
        sender_id: demoUsers[1].user_id,
        receiver_id: 'group-1',
        timestamp: new Date(Date.now() - 3200000).toISOString(), // 53 minutes ago
        read: true
      },
      {
        id: 'msg-group-1-6',
        content: 'Sounds good to me!',
        sender_id: demoUsers[2].user_id,
        receiver_id: 'group-1',
        timestamp: new Date(Date.now() - 3100000).toISOString(), // 51 minutes ago
        read: true
      },
      {
        id: 'msg-group-1-7',
        content: 'Great, I\'ll prepare some documentation before then.',
        sender_id: currentUser.user_id,
        receiver_id: 'group-1',
        timestamp: new Date(Date.now() - 3000000).toISOString(), // 50 minutes ago
        read: true
      },
      {
        id: 'msg-group-1-last',
        content: 'This is the latest group message!',
        sender_id: demoUsers[0].user_id,
        receiver_id: 'group-1',
        timestamp: new Date(Date.now() - 1200000).toISOString(), // 20 minutes ago
        read: true
      }
    ];
  }
  
  // Find the conversation
  const conversation = demoConversations.find(conv => conv.id === conversationId);
  if (!conversation) return [];
  
  // Get the other user's ID
  const otherUserId = conversation.participants.find(p => p.user_id !== currentUser.user_id)?.user_id;
  if (!otherUserId) return [];
  
  // Generate and return messages
  return generateMessages(conversationId, otherUserId, 15);
};
